/**
 * Bill of Lading PDF Generator
 * Uses @react-pdf/renderer → real vector PDF, Cairo font (Arabic + Latin).
 */
import React from 'react';
import JsBarcode from 'jsbarcode';
import BillOfLadingDocument from '../../components/pdf/BillOfLadingDocument';
import { renderPdfDocumentToBlob } from '../../components/pdf/pdfTextUtils';
import { companySettingsService } from '../system/company-settings';
import { driversService } from './drivers';
import { vehiclesService } from './vehicles';

// ─── Barcode → PNG data-URL ───────────────────────────────────────────────────
const getBarcodeDataURL = (value) => {
  const canvas = document.createElement('canvas');
  try {
    JsBarcode(canvas, String(value), {
      format: 'CODE128',
      width: 1.8,
      height: 38,
      displayValue: true,
      fontSize: 9,
      fontOptions: 'bold',
      margin: 4,
      background: '#ffffff',
      lineColor: '#000000',
    });
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
};

// ─── Main export ──────────────────────────────────────────────────────────────
export const downloadBillOfLadingPDF = async (shipment, company, options = {}) => {
  const { language = 'en', includeAmount = false } = options;

  // ── 1. Barcode ──
  const shipNum = shipment.shipmentNumber || shipment.trackingNumber || shipment.id || 'N/A';
  const barcodeDataUrl = getBarcodeDataURL(shipNum);

  // ── 2. Company settings (logo lives here, not in company doc) ──
  let companySettings = null;
  if (company?.id) {
    try {
      companySettings = await companySettingsService.getCompanySettings(company.id);
    } catch {
      // Non-critical — proceed without logo / extra settings
    }
  }

  // ── 2b. Resolve missing driver / vehicle info ──
  const enriched = { ...shipment };
  const needsDriver = (!enriched.driverName || enriched.driverName === 'Unknown Driver') && enriched.driverId;
  const needsVehicle = (!enriched.vehiclePlate || enriched.vehiclePlate === 'Unknown Vehicle') && enriched.vehicleId;
  if ((needsDriver || needsVehicle) && company?.id) {
    try {
      const [drivers, vehicles] = await Promise.all([
        needsDriver ? driversService.getDrivers(company.id) : Promise.resolve([]),
        needsVehicle ? vehiclesService.getVehicles(company.id) : Promise.resolve([]),
      ]);
      if (needsDriver) {
        const id = String(enriched.driverId);
        const d = Array.isArray(drivers) ? drivers.find(x => x && String(x.id) === id) : null;
        if (d) enriched.driverName = d.name;
      }
      if (needsVehicle) {
        const id = String(enriched.vehicleId);
        const v = Array.isArray(vehicles) ? vehicles.find(x => x && String(x.id) === id) : null;
        if (v) enriched.vehiclePlate = v.plateNumber || v.plate;
      }
    } catch {
      // Non-critical — fallback to whatever is on the shipment
    }
  }

  // ── 3. Render PDF ──
  const pdfOptions = { language, includeAmount, barcodeDataUrl };

  const blob = await renderPdfDocumentToBlob(
    <BillOfLadingDocument
      shipment={enriched}
      company={company}
      companySettings={companySettings}
      options={pdfOptions}
    />
  );

  // ── 4. Download ──
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = `BOL_${shipNum}_${language.toUpperCase()}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
